import { isValidElement, useEffect, useRef } from 'react';

import { getBackgroundDarkenStyle } from '../utils/background';

import { BackgroundImage } from '../backgroundImage';
import { getSectionInnerWrapperAttributes, getSectionRootAttributes } from './getSectionElementRenderHTML';
import styles from './Section.css';
import type { SectionElementProps } from './Section.type';

function SectionInnerWrapper(props: SectionElementProps) {
  const { children } = props;
  return <div {...getSectionInnerWrapperAttributes(props)}>{children}</div>;
}

function showSection(element: Element) {
  element.classList.add('visible');
}

export function SectionRoot(props: SectionElementProps) {
  const { element } = props;
  const {
    backgroundImage: elBackgroundImage,
    backgroundVideo,
    backgroundDarken,
    overlayColor,
    inAnimation,
  } = element.attrs || {};

  let bgImage: any;
  if (elBackgroundImage) {
    bgImage = <BackgroundImage src={elBackgroundImage} darken={backgroundDarken} overlayColor={overlayColor} />;
  }

  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!inAnimation) return;
    if (typeof window === 'undefined') return;

    const node = ref.current;
    if (!node) return;

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      showSection(node);
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            showSection(entry.target);
            obs.disconnect();
            break;
          }
        }
      },
      {
        threshold: 0,
        root: null,
        rootMargin: '0px 0px -10% 0px',
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [inAnimation]);

  return (
    <section {...getSectionRootAttributes(props)} ref={ref}>
      {isValidElement(bgImage) && (
        <div contentEditable={false} className={styles.backgroundImage}>
          {bgImage}
        </div>
      )}
      {backgroundVideo && (
        <video
          key={backgroundVideo}
          contentEditable={false}
          className={styles.backgroundVideo}
          style={getBackgroundDarkenStyle(backgroundDarken)}
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={backgroundVideo} />
        </video>
      )}
      {props.children}
    </section>
  );
}

export function SectionElement(props: SectionElementProps) {
  const { children, ...restProps } = props;

  return (
    <SectionRoot {...restProps}>
      <SectionInnerWrapper {...restProps}>{children}</SectionInnerWrapper>
    </SectionRoot>
  );
}
